"use client";

import React, { useEffect, useRef } from "react";
import type { EmbedBlock as EmbedBlockType } from "@/payload-types";
import { cn } from "@/utilities/core/cn";

interface EmbedBlockProps {
    block: EmbedBlockType;
    className?: string;
}

// Shared across all embed instances so the same external script (e.g. a widget loader)
// is fetched once and later embeds wait for it instead of running before it's ready.
const externalScriptCache = new Map<string, Promise<void>>();

const JS_SCRIPT_TYPES = new Set(["", "module", "text/javascript", "application/javascript", "text/ecmascript"]);

function cloneScript(originalScript: HTMLScriptElement): HTMLScriptElement {
    const script = document.createElement("script");

    // Copy all attributes (src, async, defer, type, charset, etc.)
    Array.from(originalScript.attributes).forEach((attr) => {
        script.setAttribute(attr.name, attr.value);
    });

    // Copy inline script content
    if (originalScript.textContent) {
        script.textContent = originalScript.textContent;
    }

    return script;
}

function loadExternalScript(originalScript: HTMLScriptElement, src: string, container: HTMLElement): Promise<void> {
    const cached = externalScriptCache.get(src);
    if (cached) return cached;

    // Loaded by something other than an embed block — assume it's ready
    if (document.querySelector(`script[src="${CSS.escape(src)}"]`)) {
        return Promise.resolve();
    }

    const script = cloneScript(originalScript);
    const type = (script.getAttribute("type") ?? "").trim().toLowerCase();

    // Non-JS types (e.g. JSON data blocks) never fire load events — don't wait on them
    if (!JS_SCRIPT_TYPES.has(type)) {
        container.appendChild(script);
        return Promise.resolve();
    }

    const promise = new Promise<void>((resolve, reject) => {
        script.onload = () => resolve();
        script.onerror = () => {
            externalScriptCache.delete(src);
            script.remove();
            reject(new Error(`Failed to load embed script: ${src}`));
        };
    });

    externalScriptCache.set(src, promise);
    container.appendChild(script);
    return promise;
}

export function EmbedBlock({ block, className }: EmbedBlockProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !block.code) return;

        // Parse the embed HTML to find script tags
        const parser = new DOMParser();
        const doc = parser.parseFromString(block.code, "text/html");
        const scripts = Array.from(doc.querySelectorAll("script"));

        let cancelled = false;
        const addedInlineScripts: HTMLScriptElement[] = [];

        // Run scripts sequentially in source order, waiting for each external script to load
        // before continuing — mirrors how the browser executes scripts in parsed HTML, so inline
        // code can rely on globals defined by a preceding <script src>.
        const run = async () => {
            for (const originalScript of scripts) {
                if (cancelled) return;

                const src = originalScript.getAttribute("src");

                if (src) {
                    try {
                        await loadExternalScript(originalScript, src, container);
                    } catch (error) {
                        console.error(error);
                    }
                    continue;
                }

                const script = cloneScript(originalScript);
                container.appendChild(script);
                addedInlineScripts.push(script);
            }
        };

        void run();

        return () => {
            cancelled = true;
            // External scripts stay loaded (their globals persist anyway) so remounts reuse them
            addedInlineScripts.forEach((script) => {
                script.remove();
            });
        };
    }, [block.code]);

    if (!block.code) return null;

    // Strip <script> tags from the HTML for the static render
    // Scripts are injected dynamically via useEffect above
    const htmlWithoutScripts = block.code
        .replace(/<script[\s\S]*?<\/script>/gi, "")
        .replace(/<script\b[^>]*\/>/gi, "");

    return (
        <div
            ref={containerRef}
            className={cn("mb-6 flex flex-col items-center [&>iframe]:max-w-full", className)}
            dangerouslySetInnerHTML={{ __html: htmlWithoutScripts }}
        />
    );
}
